import { type MouseEvent, memo, useCallback, useMemo, useState } from 'react';

import { CheckIcon, ChevronDownIcon, SearchIcon, SparklesIcon } from '@air/design-icons';
import { DropdownMenu, type DropdownMenuOptionItem, renderDropdownItems } from '@air/primitive-dropdown-menu';
import { cn } from '@air/utils-tailwind';

import { searchFieldIconSlot } from '~/components/Search/searchFieldChrome';
import { usePrivateSearchContext } from '~/providers/PrivateSearchProvider';

export type SearchCommandModeSelectorProps = {
  className?: string;
  search?: string;
  showTooltip?: boolean;
  tabIndex?: number;
};

export const SearchCommandModeSelector = memo(
  ({ className, search, showTooltip = true, tabIndex }: SearchCommandModeSelectorProps) => {
    const { canUseSmartSearch, searchType, setSearchType } = usePrivateSearchContext();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const hasSearchTerm = !!search && search.trim().length > 0;

    const handleSearchIconClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
    }, []);

    const handleOpenChange = useCallback((open: boolean) => {
      setIsDropdownOpen(open);
    }, []);

    const handleSelectKeywordSearch = useCallback(() => {
      setSearchType('keyword');
    }, [setSearchType]);

    const handleSelectSmartSearch = useCallback(() => {
      setSearchType('smart');
    }, [setSearchType]);

    const searchTypeOptions: DropdownMenuOptionItem[] = useMemo(
      () => [
        {
          id: 'keyword',
          label: 'Exact search',
          onSelect: handleSelectKeywordSearch,
          type: 'item',
          prefix: (
            <SearchIcon className="ads:text-icon-default text-gray-10" data-testid="DROPDOWN_SEARCH_ICON" size="sm" />
          ),
          suffix:
            searchType === 'keyword' ? (
              <CheckIcon
                className="ads:text-icon-default text-gray-10"
                data-testid="EXACT_SEARCH_CHECK_ICON"
                size="sm"
              />
            ) : undefined,
        },
        {
          id: 'smart',
          label: 'Smart search',
          onSelect: handleSelectSmartSearch,
          type: 'item',
          prefix: (
            <SparklesIcon className="ads:text-icon-default text-gray-10" data-testid="DROPDOWN_AI_ICON" size="sm" />
          ),
          suffix:
            searchType === 'smart' ? (
              <CheckIcon
                className="ads:text-icon-default text-gray-10"
                data-testid="SMART_SEARCH_CHECK_ICON"
                size="sm"
              />
            ) : undefined,
        },
      ],
      [handleSelectKeywordSearch, handleSelectSmartSearch, searchType],
    );

    const searchIcon = (
      <button
        aria-label={searchType === 'smart' ? 'Smart search' : 'Exact search'}
        className={cn(
          'flex cursor-pointer items-center gap-0.5 rounded-full ads:bg-transparent bg-gray-1 px-1 py-0.5 ads:hover:bg-hover-subtle hover:bg-gray-4',
          '[outline:none] focus-visible:ring-0 focus-visible:[outline:none]',
          'ui2026:h-8 ui2026:justify-center ui2026:bg-transparent ui2026:px-2 ui2026:py-0 ads:text-icon-subtle ui2026:text-gray-9 hover:ads:bg-hover-subtle ui2026:hover:bg-gray-4 ads:hover:text-icon-emphasis ui2026:hover:text-gray-12',
          isDropdownOpen && 'ads:bg-hover-subtle bg-gray-4 ads:text-icon-emphasis ui2026:text-gray-12',
          className,
        )}
        data-testid="SEARCH_COMMAND_MODE_SELECTOR_BUTTON"
        onClick={handleSearchIconClick}
        tabIndex={tabIndex}
        type="button"
      >
        {searchType === 'smart' ? (
          <SparklesIcon
            className={cn(
              'm-0.5 ui2026:m-0 shrink-0',
              hasSearchTerm
                ? 'ads:text-ads-violet-9 text-violet-9'
                : 'ads:text-current text-gray-10 ui2026:text-current',
            )}
            data-testid="SEARCH_COMMAND_MODE_SELECTOR_AI_ICON"
            size="sm"
          />
        ) : (
          <SearchIcon
            className="m-0.5 ui2026:m-0 shrink-0 ads:text-current text-gray-10 ui2026:text-current"
            data-testid="SEARCH_COMMAND_MODE_SELECTOR_SEARCH_ICON"
            size="sm"
          />
        )}
        <ChevronDownIcon
          className="size-4 ui2026:size-3 shrink-0 ads:text-current text-gray-10 ui2026:text-current"
          data-testid="TRIANGLE_DOWN_ICON"
          size="sm"
        />
      </button>
    );

    if (!canUseSmartSearch) {
      return (
        <span className={cn(searchFieldIconSlot(), className)}>
          <SearchIcon
            className="shrink-0 ads:text-icon-default text-gray-10"
            data-testid="FALLBACK_SEARCH_ICON"
            size="sm"
          />
        </span>
      );
    }

    return (
      <DropdownMenu
        align="start"
        className="w-[280px]"
        onOpenChange={handleOpenChange}
        open={isDropdownOpen}
        side="bottom"
        sideOffset={4}
        size="large"
        trigger={searchIcon}
        triggerTooltip={showTooltip ? 'Search mode' : undefined}
      >
        {renderDropdownItems({ options: searchTypeOptions })}
      </DropdownMenu>
    );
  },
);

SearchCommandModeSelector.displayName = 'SearchCommandModeSelector';
